import { useCallback, useMemo, useState } from "react";
import { EnabledAction, ScopeOption } from "../../../../../../models/CommonModel";
import { SortedModelsMap } from "../../CommonModelUtils";
import { fieldEditKey, modelEditKey, ScopeEditsMap } from "../types";
import {
  applyFieldScope,
  applyModelCapabilityScope,
  scopeForField,
  scopeForModelCapability,
} from "../utils/scopeMapping";

/**
 * Holds unsaved scope edits for the scopes table. Toggling records a target
 * scope locally (no API call); `editedModels` applies the pending edits onto the base
 * models so the switches and field counts reflect the in-progress state.
 */
export const useScopeEdits = (baseModels: SortedModelsMap, areOptionalScopesEnabled: boolean) => {
  // state
  const [edits, setEdits] = useState<ScopeEditsMap>({});

  // event handlers
  const setModelScope = useCallback(
    (modelName: string, capability: EnabledAction, scope: ScopeOption) => {
      const baseModel = baseModels[modelName];
      const baseScope = baseModel
        ? scopeForModelCapability(baseModel, capability, areOptionalScopesEnabled)
        : undefined;
      const key = modelEditKey(modelName, capability);
      setEdits((prev) => {
        const next = { ...prev };
        // Re-selecting the original scope is a no-op; drop the key so it doesn't count or PATCH.
        if (scope === baseScope) delete next[key];
        else next[key] = { kind: "model", modelName, capability, scope };
        return next;
      });
    },
    [baseModels, areOptionalScopesEnabled],
  );

  const setFieldScope = useCallback(
    (modelName: string, fieldName: string, scope: ScopeOption) => {
      const baseField = baseModels[modelName]?.fields[fieldName];
      const baseScope = baseField ? scopeForField(baseField, areOptionalScopesEnabled) : undefined;
      const key = fieldEditKey(modelName, fieldName);
      setEdits((prev) => {
        const next = { ...prev };
        // Re-selecting the original scope is a no-op; drop the key so it doesn't count or PATCH.
        if (scope === baseScope) delete next[key];
        else next[key] = { kind: "field", modelName, fieldName, scope };
        return next;
      });
    },
    [baseModels, areOptionalScopesEnabled],
  );

  const reset = useCallback(() => setEdits({}), []);

  // derived state
  const editedModels = useMemo<SortedModelsMap>(() => {
    if (Object.keys(edits).length === 0) return baseModels;
    const next: SortedModelsMap = { ...baseModels };
    Object.values(edits).forEach((edit) => {
      const model = next[edit.modelName];
      if (!model) return;
      if (edit.kind === "model") {
        next[edit.modelName] = applyModelCapabilityScope(model, edit.capability, edit.scope);
      } else {
        const field = model.fields[edit.fieldName];
        if (!field) return;
        next[edit.modelName] = {
          ...model,
          fields: { ...model.fields, [edit.fieldName]: applyFieldScope(field, edit.scope) },
        };
      }
    });
    return next;
  }, [baseModels, edits]);

  // count of distinct objects with at least one pending change
  const editedCount = useMemo(
    () => new Set(Object.values(edits).map((edit) => edit.modelName)).size,
    [edits],
  );

  return { editedModels, edits, editedCount, setModelScope, setFieldScope, reset };
};
